/**
 * Formats currency values with proper thousands separators and currency symbol
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency symbol to use
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = "€") => {
	return `${amount.toFixed(2).replace(".", ",")} ${currency}`;
};

/**
 * Validates a VAT ID based on country-specific patterns
 * @param {string} country - Country code (DE, AT, CH)
 * @param {string} vatId - VAT ID to validate
 * @returns {boolean} Whether the VAT ID is valid
 */
export const validateVatId = (country, vatId) => {
	const patterns = {
		DE: /^DE[0-9]{9}$/,
		AT: /^ATU[0-9]{8}$/,
		CH: /^CHE[0-9]{9}(MWST|TVA|IVA)?$/
	};
	
	// Add country prefix if missing
	let fullVatId = vatId;
	if (country === "DE" && !vatId.startsWith("DE")) fullVatId = `DE${vatId}`;
	else if (country === "AT" && !vatId.startsWith("AT")) fullVatId = `AT${vatId}`;
	else if (country === "CH" && !vatId.startsWith("CHE")) fullVatId = `CHE${vatId}`;
	
	return patterns[country] ? patterns[country].test(fullVatId) : true;
};

/**
 * Formats date and time for event display
 * @param {string} start - Start date/time
 * @param {string} end - End date/time (optional)
 * @returns {string} Formatted date/time range
 */
export const formatEventDate = (start, end) => {
	if (!start) return "";
	try {
		const startDate = new Date(start);
		const dateStr = startDate.toLocaleDateString("de-DE", { year: "numeric", month: "numeric", day: "numeric" });
		const timeStr = startDate.toLocaleTimeString("de-DE", { hour: "numeric", minute: "numeric" });
		if (end) {
			const endDate = new Date(end);
			const endTimeStr = endDate.toLocaleTimeString("de-DE", { hour: "numeric", minute: "numeric" });
			return `${dateStr}, ${timeStr} - ${endTimeStr}`;
		}
		return `${dateStr}, ${timeStr}`;
	} catch {
		return start;
	}
};

/**
 * Extracts and formats the tax rate from article data
 * @param {Object} article - The article object
 * @returns {number} The tax rate as a percentage (e.g., 19 for 19%)
 */
export const extractTaxRate = (article) => {
	// Try to get tax rate from different possible locations in the article object
	const taxPercentage = article?.tax_percentage || 
		article?.article_price?.tax_percentage ||
		article?.instance?.tax_percentage || 
		article?.instance?.cost?.tax_percentage || 
		null;
	
	// If we found a percentage directly, return it
	if (taxPercentage !== null && taxPercentage !== undefined) {
		return Number(taxPercentage);
	}
	
	// If not, try to calculate from amounts
	const grossAmount = Number(article?.article_price?.total_gross_amount || 0);
	const netAmount = Number(article?.article_price?.total_net_amount || 0);
	
	if (grossAmount > 0 && netAmount > 0) {
		const taxRatio = (grossAmount / netAmount) - 1;
		return Math.round(taxRatio * 100);
	}
	
	// Default to standard German VAT rate if nothing else is available
	return 19;
};

/**
 * Calculates the tax amount for a single item
 * @param {Object} item - The item object
 * @returns {number} The calculated tax amount
 */
export const calculateTaxAmount = (item) => {
	const price = Number(item.price || 0);
	const quantity = parseInt(item.quantity || 1, 10);
	const taxRate = item.taxRate || extractTaxRate(item.originalArticle) || 19;
	
	// Calculate using net-to-gross formula
	const grossAmount = price * quantity;
	const netAmount = grossAmount / (1 + taxRate / 100);
	return grossAmount - netAmount;
};

/**
 * Groups tax amounts by tax rate, properly accounting for quantities
 * @param {Array} items - Array of order items
 * @returns {Object} Object with tax rates as keys and amounts as values
 */
export const groupTaxesByRate = (items = []) => {
	const taxGroups = {};
	
	items.forEach(item => {
		// Get tax rate for this item
		const taxRate = item.taxRate || extractTaxRate(item.originalArticle) || 19;
		
		// Calculate the tax amount for this item
		let taxAmount = 0;
		const quantity = parseInt(item.quantity || 1, 10);
		const price = Number(item.price || 0);
		
		// Calculate tax amount: grossAmount - netAmount
		const grossAmount = price * quantity;
		const netAmount = grossAmount / (1 + taxRate / 100);
		taxAmount = grossAmount - netAmount;
		
		// Initialize tax rate group if it doesn't exist
		if (!taxGroups[taxRate]) {
			taxGroups[taxRate] = 0;
		}
		
		// Add tax amount to corresponding group
		taxGroups[taxRate] += taxAmount;
	});
	
	return taxGroups;
};

/**
 * Process API items for display in the checkout
 * @param {Object} orderData - Order data from API
 * @returns {Array} Processed items for display
 */
export const processApiItems = (orderData) => {
	if (!orderData?.item?.article || !Array.isArray(orderData.item.article)) return [];
	
	return orderData.item.article.map(article => {
		const instanceData = article.instance || {};
		const articleEvent = instanceData.event || {};
		const articleCost = instanceData.cost || {};
		
		// Convert count from string to number for display purposes
		const count = parseInt(article.count || 1, 10);
		
		// Extract tax information
		const taxRate = extractTaxRate(article);
		
		// Calculate price and total amounts
		const price = Number(articleCost.sale_price || 0);
		const grossAmount = price * count;
		const netAmount = grossAmount / (1 + taxRate / 100);
		const taxAmount = grossAmount - netAmount;
		
		return {
			id: article.id || instanceData.id,
			product_name: instanceData.title || "Article",
			description: articleEvent.title
				? `${articleEvent.title} (${formatEventDate(articleEvent.date, articleEvent.end_date)})`
				: instanceData.desc_long || instanceData.desc || "",
			price: price,
			quantity: count,
			event_date: articleEvent.date,
			event_end_date: articleEvent.end_date,
			originalArticle: article,
			// Store event details directly with their correct paths
			originalEvent: articleEvent,
			taxRate: taxRate,
			taxAmount: taxAmount,
			netAmount: netAmount,
			grossAmount: grossAmount
		};
	});
};

/**
 * Validates form data for checkout
 * @param {Object} formData - Form data to validate
 * @param {Array} expandedTickets - Tickets data
 * @returns {Object} Validation errors
 */
export const validateCheckoutForm = (formData, expandedTickets) => {
	const errors = {};
	
	// Basic customer information
	if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) errors.email = "Gültige E-Mail-Adresse erforderlich";
	if (!formData.name.trim()) errors.name = "Vorname ist erforderlich";
	if (!formData.lastName.trim()) errors.lastName = "Nachname ist erforderlich";
	
	// Address fields for non-PayPal payment methods
	if (formData.paymentMethod !== "paypal") {
		if (!formData.street.trim()) errors.street = "Straße ist erforderlich";
		if (!formData.zipCode.trim()) errors.zipCode = "PLZ ist erforderlich";
		if (!formData.city.trim()) errors.city = "Stadt ist erforderlich";
	}
	
	// Company information
	if (formData.isCompany) {
		if (!formData.companyName.trim()) errors.companyName = "Firmenname ist erforderlich";
		if (!formData.vatId.trim()) errors.vatId = "USt-IdNr. ist erforderlich";
		else if (!validateVatId(formData.vatCountry, formData.vatId)) errors.vatId = "Ungültiges USt-IdNr. Format";
	}
	
	// Terms acceptance
	if (!formData.acceptTerms) errors.acceptTerms = "Bitte akzeptieren Sie die AGB und Datenschutzbestimmungen";
	
	// Validate tickets
	expandedTickets.forEach(ticket => {
		// For tickets assigned to others, validate invitee information
		if (!ticket.isForOrderer) {
			if (!ticket.invitee.email || !/\S+@\S+\.\S+/.test(ticket.invitee.email)) {
				errors[`ticket_email_${ticket.id}`] = "Gültige E-Mail erforderlich";
			}
			
			if (!ticket.invitee.firstName.trim()) {
				errors[`ticket_firstName_${ticket.id}`] = "Vorname erforderlich";
			}
			
			if (!ticket.invitee.lastName.trim()) {
				errors[`ticket_lastName_${ticket.id}`] = "Nachname erforderlich";
			}
		} 
		// For tickets assigned to the orderer, validate preferences if required
		else if (ticket.requiredAgendaAmount > 0) {
			if (!ticket.preferences || ticket.preferences.length < ticket.requiredAgendaAmount) {
				errors[`ticket_preferences_${ticket.id}`] = `Bitte wählen Sie ${ticket.requiredAgendaAmount === 1 ? 'genau 1 Thema' : `genau ${ticket.requiredAgendaAmount} Themen`}`;
			}
		}
	});
	
	return errors;
};

/**
 * Calculates the order totals from items
 * @param {Array} items - The order items
 * @param {Object} discountInfo - Optional discount information
 * @returns {Object} Order totals
 */
export const calculateOrderTotals = (items, discountInfo = null) => {
	// Group taxes by rate
	const taxGroups = groupTaxesByRate(items);
	
	// Calculate subtotal (before discount)
	const subtotal = items.reduce((sum, item) => {
		return sum + (item.price * item.quantity);
	}, 0);
	
	// Calculate discount if applicable
	const discount = discountInfo ? Number(discountInfo.value || 0) : 0;
	
	// Calculate discounted subtotal
	const discountedSubtotal = Math.max(0, subtotal - discount);
	
	// Calculate total tax
	const totalTax = Object.values(taxGroups).reduce((sum, taxAmount) => sum + taxAmount, 0);
	
	// Calculate final total
	const total = discountedSubtotal;
	
	return {
		subtotal,
		discount,
		discountedSubtotal,
		tax: totalTax,
		total,
		taxGroups
	};
};
